export interface OfferLocation {
  name: string;
  address: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  lonlat: string;
  active: boolean;
}

// This matches the NN/ALV equivalent on the DB.
// AP Equivalent is OfferStatusTypeCodes
export enum OfferStatus {
  Open = 'open', // Active to Members
  Restricted = 'restricted', // Invisible
  Public = 'public', // Active to Public
  Unavailable = 'unavailable', // Unavailable
  Blocked = 'blocked', // Disabled
}

export interface Offer {
  id: string;
  type: string;
  brand: string;
  title: string;
  categories: string[];
  logoUrl: string;
  coverImageUrl: string;
  mainCategoryIconUrl: string | null;
  shortDescription: string | null;
  longDescription: string | DescriptionOption[];
  terms: OfferTerms[];
  howToUse: string | HowToUseOption[];
  channels: string[];
  email: string | null;
  phone: string | null;
  promocode: string | null;
  trackingUrlAvailable: boolean;
  website: string | null;
  availableUniquePromoCode?: boolean;
  useUniquePromoCode?: boolean;
  gallery: string;
  startTime: string | null;
  endTime: string | null;
  status: OfferStatus;
  active: boolean;
  locations: OfferLocation[];
  source?: OfferSource;
}

export type OfferSource = 'partner' | 'points_boost';

// Possible offer categories are:
// food_and_drink fashion retail technology sports health_and_beauty entertainment
// travel online pubs_and_clubs automotive_service protection others
// https://kaligo.slack.com/archives/C015TFYAVEY/p1598255639006800?thread_ts=1596792392.040600&cid=C015TFYAVEY
export type OfferSupportedCategories =
  | 'dining'
  | 'shopping'
  | 'lifestyle'
  | 'travel'
  | 'others';

export type VmaOfferSupportedCategories =
  | OfferSupportedCategories
  // VMA specific mapping due to https://kaligo.atlassian.net/browse/VMA2-1055
  // and https://kaligo.atlassian.net/browse/PN-184
  | 'fashion'
  | 'retail'
  | 'tech'
  | 'health_and_beauty'
  | 'entertainment';

export type BnzOfferSupportedCategories =
  | OfferSupportedCategories
  // BNZ specific mapping due to https://kaligo.atlassian.net/browse/RCF-1164
  // and https://kaligo.atlassian.net/browse/BNZ-570
  | 'automotive_service'
  | 'health_and_beauty'
  | 'food_and_drink'
  | 'retail'
  | 'sports'
  | 'home_and_garden'
  | 'fashion'
  | 'technology';

export interface OfferQueryParams {
  channel?: '' | 'online' | 'offline' | 'phone';
  category?:
    | ''
    | OfferSupportedCategories
    | BnzOfferSupportedCategories
    | VmaOfferSupportedCategories;
  city?: '' | 'sydney' | 'melbourne' | 'adelaide' | 'perth' | 'brisbane';
  sort?:
    | '-score_by_recommended'
    | '-score_by_best_rated'
    | '-created_at'
    | 'brand'
    | '-brand'
    | 'end_time'
    | '-score_by_distance';
  searchByBrand?: string;
  source?: OfferSource;
}

export interface OfferSortOption {
  name: string;
  value: OfferQueryParams['sort'];
}

export interface OfferCategoryOption {
  name: string;
  value: OfferQueryParams['category'];
}

export interface OfferChannelOption {
  name: string;
  value: OfferQueryParams['channel'];
}

export interface OfferCityOption {
  name: string;
  value: OfferQueryParams['city'];
}

export interface OfferTerms {
  header: string;
  body: string;
}

export interface HowToUseOption {
  header: string;
  body: string;
}

export interface DescriptionOption {
  header: string;
  body: string;
}

// TODO: Should unify the PaginationOptions as a shared interface for Merchandises and other
export interface OfferPaginationOptions {
  page: number;
  pageSize: number;
}

export interface FormattedOfferData {
  offerMap: { [key: string]: Offer };
  offerIds: Offer['id'][];
  count: number;
}

export interface FormattedPaginatedOfferData extends FormattedOfferData {
  totalPageCount: number;
}
